/**
 * Material Library — org-wide catalogue of reusable material entries
 * (Plywood / Laminate / Hardware / Paint / etc.). Material requests
 * on projects pick from this catalogue via `libraryItemId`.
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/materialLibrary/types.ts
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/materialLibrary/materialLibrary.ts
 *
 * Schema parity is mandatory — mobile + web read AND write the
 * same `materialLibrary/{itemId}` docs.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

// ───── Categories ─────────────────────────────────────────────

export type MaterialCategory =
  | 'plywood'
  | 'laminate'
  | 'hardware'
  | 'paint'
  | 'adhesive'
  | 'glass'
  | 'electrical'
  | 'plumbing'
  | 'tiles_stone'
  | 'cement_sand'
  | 'other';

export type CategoryField = {
  key: string;
  label: string;
  placeholder: string;
  type: 'text' | 'chips';
  options?: string[];
};

export type CategoryConfig = {
  key: MaterialCategory;
  label: string;
  /** Phosphor icon class (web port of mobile Ionicons). */
  icon: string;
  /** Tint colour (used for the category pill + grid card avatar). */
  color: string;
  /** Pill bg tint (lightened). */
  tintBg: string;
  /** Default unit suggested when adding a new item in this category. */
  defaultUnit: string;
  fields: CategoryField[];
};

/** Mirrors mobile's MATERIAL_CATEGORIES verbatim (same enum keys,
 *  same labels, same chip options). Phosphor icons + iio.css-friendly
 *  hex colours substituted for the React Native equivalents. */
export const MATERIAL_CATEGORIES: CategoryConfig[] = [
  {
    key: 'plywood',
    label: 'Plywood',
    icon: 'ph ph-stack',
    color: '#92400E',
    tintBg: '#FEF3C7',
    defaultUnit: 'sqft',
    fields: [
      { key: 'brand', label: 'Brand', placeholder: 'e.g. Greenply, Century, Kitply', type: 'text' },
      { key: 'variety', label: 'Type', placeholder: '', type: 'chips', options: ['BWR', 'MR', 'Commercial', 'Marine', 'Flush Door'] },
      { key: 'make', label: 'Thickness', placeholder: '', type: 'chips', options: ['4mm', '6mm', '8mm', '12mm', '16mm', '18mm', '19mm', '25mm'] },
      { key: 'size', label: 'Size', placeholder: '', type: 'chips', options: ['8x4 ft', '7x4 ft', '6x4 ft', '6x3 ft'] },
    ],
  },
  {
    key: 'laminate',
    label: 'Laminate',
    icon: 'ph ph-palette',
    color: '#7C3AED',
    tintBg: '#F5F3FF',
    defaultUnit: 'sqft',
    fields: [
      { key: 'brand', label: 'Brand', placeholder: 'e.g. Merino, Greenlam, Century', type: 'text' },
      { key: 'variety', label: 'Code', placeholder: 'e.g. 22003 RGL', type: 'text' },
      { key: 'make', label: 'Finish', placeholder: '', type: 'chips', options: ['Matte', 'Gloss', 'Suede', 'Texture', 'Satin', 'Anti-Finger'] },
      { key: 'size', label: 'Thickness', placeholder: '', type: 'chips', options: ['0.8mm', '1mm', '1.25mm', '1.5mm'] },
    ],
  },
  {
    key: 'hardware',
    label: 'Hardware',
    icon: 'ph ph-wrench',
    color: '#475569',
    tintBg: '#F1F5F9',
    defaultUnit: 'pcs',
    fields: [
      { key: 'variety', label: 'Type', placeholder: '', type: 'chips', options: ['Hinge', 'Channel', 'Handle', 'Lock', 'Knob', 'Drawer Slide', 'Aldrop', 'Tower Bolt', 'Magnet', 'Soft Close'] },
      { key: 'brand', label: 'Brand', placeholder: 'e.g. Hettich, Hafele, Ebco', type: 'text' },
      { key: 'make', label: 'Finish', placeholder: '', type: 'chips', options: ['SS', 'Brass', 'Chrome', 'Black', 'Antique', 'Rose Gold', 'PVD'] },
      { key: 'size', label: 'Size', placeholder: 'e.g. 4 inch, 18 inch, 450mm', type: 'text' },
    ],
  },
  {
    key: 'paint',
    label: 'Paint',
    icon: 'ph ph-paint-brush',
    color: '#0891B2',
    tintBg: '#CFFAFE',
    defaultUnit: 'litres',
    fields: [
      { key: 'brand', label: 'Brand', placeholder: 'e.g. Asian Paints, Berger, Dulux', type: 'text' },
      { key: 'variety', label: 'Type', placeholder: '', type: 'chips', options: ['Emulsion', 'Enamel', 'Primer', 'Putty', 'POP', 'Wood Finish', 'Sealer', 'Thinner'] },
      { key: 'make', label: 'Finish', placeholder: '', type: 'chips', options: ['Matt', 'Satin', 'Gloss', 'Semi-Gloss'] },
      { key: 'size', label: 'Color / Shade', placeholder: 'e.g. White, L123, Custom', type: 'text' },
    ],
  },
  {
    key: 'adhesive',
    label: 'Adhesive',
    icon: 'ph ph-flask',
    color: '#CA8A04',
    tintBg: '#FEF9C3',
    defaultUnit: 'kg',
    fields: [
      { key: 'brand', label: 'Brand', placeholder: 'e.g. Fevicol, Pidilite, Araldite', type: 'text' },
      { key: 'variety', label: 'Type', placeholder: '', type: 'chips', options: ['Fevicol SH', 'Fevicol Marine', 'Dendrite', 'Silicon', 'Epoxy', 'Wood Glue', 'Contact Adhesive'] },
      { key: 'size', label: 'Pack Size', placeholder: 'e.g. 1kg, 5kg, 50kg', type: 'text' },
    ],
  },
  {
    key: 'glass',
    label: 'Glass',
    icon: 'ph ph-square',
    color: '#0EA5E9',
    tintBg: '#E0F2FE',
    defaultUnit: 'sqft',
    fields: [
      { key: 'variety', label: 'Type', placeholder: '', type: 'chips', options: ['Plain', 'Toughened', 'Frosted', 'Mirror', 'Tinted', 'Lacquered', 'Etched'] },
      { key: 'brand', label: 'Brand', placeholder: 'e.g. Saint Gobain, Asahi, Modi', type: 'text' },
      { key: 'make', label: 'Thickness', placeholder: '', type: 'chips', options: ['4mm', '5mm', '6mm', '8mm', '10mm', '12mm'] },
      { key: 'size', label: 'Size', placeholder: 'e.g. Custom, 8x4 ft', type: 'text' },
    ],
  },
  {
    key: 'electrical',
    label: 'Electrical',
    icon: 'ph ph-lightning',
    color: '#DC2626',
    tintBg: '#FEE2E2',
    defaultUnit: 'pcs',
    fields: [
      { key: 'variety', label: 'Type', placeholder: '', type: 'chips', options: ['Wire', 'Switch', 'Socket', 'Light', 'Fan', 'MCB', 'DB Box', 'Conduit', 'LED Strip', 'Downlight'] },
      { key: 'brand', label: 'Brand', placeholder: 'e.g. Havells, Polycab, Legrand', type: 'text' },
      { key: 'make', label: 'Specification', placeholder: 'e.g. 1.5 sqmm, 6A, 10W', type: 'text' },
      { key: 'size', label: 'Color / Model', placeholder: 'e.g. White, Warm White', type: 'text' },
    ],
  },
  {
    key: 'plumbing',
    label: 'Plumbing',
    icon: 'ph ph-drop',
    color: '#2563EB',
    tintBg: '#DBEAFE',
    defaultUnit: 'pcs',
    fields: [
      { key: 'variety', label: 'Type', placeholder: '', type: 'chips', options: ['Pipe', 'Fitting', 'Tap', 'Shower', 'WC', 'Basin', 'Drain', 'Valve', 'Geyser'] },
      { key: 'brand', label: 'Brand', placeholder: 'e.g. Jaquar, Kohler, Astral', type: 'text' },
      { key: 'make', label: 'Material', placeholder: '', type: 'chips', options: ['CPVC', 'PVC', 'GI', 'SS', 'Brass', 'Chrome'] },
      { key: 'size', label: 'Size', placeholder: 'e.g. 15mm, 20mm, ½ inch', type: 'text' },
    ],
  },
  {
    key: 'tiles_stone',
    label: 'Tiles & Stone',
    icon: 'ph ph-grid-four',
    color: '#059669',
    tintBg: '#D1FAE5',
    defaultUnit: 'sqft',
    fields: [
      { key: 'variety', label: 'Type', placeholder: '', type: 'chips', options: ['Floor Tile', 'Wall Tile', 'Vitrified', 'Ceramic', 'Granite', 'Marble', 'Quartz', 'Mosaic'] },
      { key: 'brand', label: 'Brand', placeholder: 'e.g. Kajaria, Somany, RAK', type: 'text' },
      { key: 'size', label: 'Size', placeholder: '', type: 'chips', options: ['1x1 ft', '2x1 ft', '2x2 ft', '4x2 ft', '4x4 ft', 'Custom'] },
      { key: 'make', label: 'Finish', placeholder: '', type: 'chips', options: ['Glossy', 'Matt', 'Satin', 'Polished', 'Honed', 'Rustic'] },
    ],
  },
  {
    key: 'cement_sand',
    label: 'Cement & Sand',
    icon: 'ph ph-cube',
    color: '#78716C',
    tintBg: '#F5F5F4',
    defaultUnit: 'bags',
    fields: [
      { key: 'variety', label: 'Type', placeholder: '', type: 'chips', options: ['Cement', 'Sand', 'Aggregate', 'Ready Mix', 'White Cement', 'Tile Adhesive'] },
      { key: 'brand', label: 'Brand', placeholder: 'e.g. UltraTech, ACC, Ambuja', type: 'text' },
      { key: 'make', label: 'Grade', placeholder: '', type: 'chips', options: ['OPC 43', 'OPC 53', 'PPC', 'PSC', 'M Sand', 'River Sand'] },
      { key: 'size', label: 'Pack Size', placeholder: 'e.g. 50kg, 1 CFT', type: 'text' },
    ],
  },
  {
    key: 'other',
    label: 'Other',
    icon: 'ph ph-dots-three',
    color: '#6B7280',
    tintBg: '#F3F4F6',
    defaultUnit: 'pcs',
    fields: [
      { key: 'brand', label: 'Brand', placeholder: 'Brand name', type: 'text' },
      { key: 'variety', label: 'Variety', placeholder: 'Type or variety', type: 'text' },
      { key: 'make', label: 'Make', placeholder: 'Make or specification', type: 'text' },
      { key: 'size', label: 'Size', placeholder: 'Size or dimension', type: 'text' },
    ],
  },
];

export function getCategoryConfig(key: MaterialCategory | string): CategoryConfig {
  return (
    MATERIAL_CATEGORIES.find((c) => c.key === key) ??
    MATERIAL_CATEGORIES[MATERIAL_CATEGORIES.length - 1]
  );
}

// ───── Units (shared with material request items) ─────

export const UNIT_OPTIONS: { key: string; label: string }[] = [
  { key: 'pcs', label: 'pcs' },
  { key: 'kg', label: 'kg' },
  { key: 'bags', label: 'bags' },
  { key: 'sqft', label: 'sqft' },
  { key: 'rft', label: 'rft' },
  { key: 'cft', label: 'cft' },
  { key: 'litres', label: 'litres' },
  { key: 'meters', label: 'meters' },
  { key: 'tons', label: 'tons' },
  { key: 'sets', label: 'sets' },
  { key: 'sheet', label: 'sheet' },
  { key: 'pair', label: 'pair' },
];

// ───── Item shape ─────

export type MaterialLibraryItem = {
  id: string;
  orgId: string;
  category: MaterialCategory;
  name: string;
  brand: string;
  variety: string;
  make: string;
  size: string;
  unit: string;
  defaultRate?: number;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
};

// ───── Writes ─────

export type CreateLibraryItemInput = {
  orgId: string;
  category: MaterialCategory;
  name: string;
  brand: string;
  variety: string;
  make: string;
  size: string;
  unit: string;
  defaultRate?: number;
  createdBy: string;
};

export async function createLibraryItem(
  input: CreateLibraryItemInput,
): Promise<string> {
  const payload: Record<string, unknown> = {
    orgId: input.orgId,
    category: input.category,
    name: input.name,
    brand: input.brand,
    variety: input.variety,
    make: input.make,
    size: input.size,
    unit: input.unit,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
  };
  if (input.defaultRate != null && input.defaultRate > 0) {
    payload.defaultRate = input.defaultRate;
  }
  const ref = await addDoc(collection(db, 'materialLibrary'), payload);
  return ref.id;
}

export async function updateLibraryItem(
  id: string,
  data: Partial<Omit<CreateLibraryItemInput, 'orgId' | 'createdBy'>>,
): Promise<void> {
  const updates: Record<string, unknown> = {};
  if (data.category !== undefined) updates.category = data.category;
  if (data.name !== undefined) updates.name = data.name;
  if (data.brand !== undefined) updates.brand = data.brand;
  if (data.variety !== undefined) updates.variety = data.variety;
  if (data.make !== undefined) updates.make = data.make;
  if (data.size !== undefined) updates.size = data.size;
  if (data.unit !== undefined) updates.unit = data.unit;
  if (data.defaultRate !== undefined) {
    updates.defaultRate =
      data.defaultRate > 0 ? data.defaultRate : deleteField();
  }
  await updateDoc(doc(db, 'materialLibrary', id), updates);
}

export async function deleteLibraryItem(id: string): Promise<void> {
  await deleteDoc(doc(db, 'materialLibrary', id));
}
